import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Icon,
  Box,
  Fab,
  useTheme,
  useMediaQuery,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  AssignmentInd as SelectTypeIcon,
  Edit as CompleteFormIcon,
  CheckCircle as ValidateIcon,
  Business,
  Person,
  ArrowLeft,
  ArrowRight,
  Info,
  MoneyOutlined,
  Send,
} from '@mui/icons-material';
import { RoundedButton } from 'ui/atoms/RoundedButton/RoundedButton';
import CompanyForm from 'components/CompanyForm/CompanyForm';
import AuthPage from 'components/AuthForm/AuthForm';
import { useLogin } from 'Contexts/AuthProvider/AuthProvider';
import { useEnrollForm } from 'Contexts/EnrollProvider/EnrollProvider';
import { useDialog } from 'Contexts/Modals/ModalProvider';
import Lottie from 'react-lottie';
import animationData from '../../lotties-animations/animation.json';
import animationDataReview from '../../lotties-animations/review.json';
import { updateEnroll } from 'Helpers/update-enroll';
import { useNavigate } from 'react-router-dom';
import './styles.css';
import FundingStep from 'components/Steps/FundingStep/FundingStep';
import FormValidation from 'components/Steps/FormValidation/FormValidation';
import { RejectedEnrolled } from 'components/RejectedEnroll/RejectedEnrolled';
import ClickAgreeStep from 'components/Steps/ClickAgreeStep/ClickAgreeStep';
import ConfirmationMenssage from 'components/Steps/ConfirmationMenssage/ConfirmationMenssage';
import PlaidStep from 'components/Steps/PlaidStep/PlaidStep.jsx';
import MenssgePostDay from 'components/Steps/MenssagePostDay/MenssgePostDay';
import ProviderSelectionStep from 'components/Steps/ProviderSelectionStep/ProviderSelectionStep';
import { isProviderEnabled, fetchEnabledProviders } from 'Helpers/providersApi';
import { fetchRequestByUser } from 'Helpers/request-by-user';
import {
  persistEnrollmentProgress,
  readEnrollmentProgress,
  resolveEnrollmentProgress,
  resolvePaymentSetupProgress,
  PROVIDER_STEP,
} from 'Helpers/enrollment-progress';
import { newServer } from 'constants/constants';
import {
  DEFERRED_OPTION,
  REQUEST_ORIGIN,
  ProviderSelection,
  providerFlags,
  toBackendChoice,
} from 'constants/providers';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { styled } from '@mui/material/styles';

export interface EnrollmentStepsProps {
  /**
   * `payment-setup` = el abogado ya tiene portal y vuelve a completar el KYC.
   * No crea subdominio, no ofrece "Configure later" y oculta proveedores apagados.
   */
  source?: 'enroll' | 'payment-setup';
  /** Tras enviar el KYC desde el portal: el padre muestra "en revisión". */
  onSubmitted?: () => void;
}

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  /**
   * El violeta literal que habia aqui no pertenecia a ninguna paleta (ni al
   * indigo de marca ni a MUI). Ahora el tramo activo/recorrido usa el color
   * primario del tema y el tramo pendiente usa el divisor, asi que el stepper
   * responde al cambio de tema en vez de quedar morado siempre.
   */
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.divider,
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

/**
 * Propósito: Completa dbaName al enviar el enrollment.
 * El DBA vive en el formulario Dwolla (doingBusinessAs) y a veces no llega al payload USIO.
 */
const resolveMerchantDbaName = (
  usioPayload: { dbaName?: string; legalName?: string } | undefined,
  dwollaPayload: { doingBusinessAs?: string; businessName?: string } | undefined
) => {
  const storedDbaName = usioPayload?.dbaName?.trim() || '';
  const doingBusinessAs = dwollaPayload?.doingBusinessAs?.trim() || '';
  const legalName = usioPayload?.legalName?.trim() || dwollaPayload?.businessName?.trim() || '';

  /**
   * Output:
   * @returns {string} DBA a persistir en info_usio. Prioridad: dbaName → doingBusinessAs → legalName
   */
  return storedDbaName || doingBusinessAs || legalName;
};

const EnrollmentForm = ({
  source = 'enroll',
  onSubmitted,
}: EnrollmentStepsProps) => {
  const [enrollmentType, setEnrollmentType] = useState('');
  const [submitionOnReview, setSubmitionOnReview] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [displayNavigationButtons, setDisplayNavigationButtons] = useState(true);
  /** Estado del paso de proveedor: mientras se crea la solicitud diferida. */
  const [providerSaving, setProviderSaving] = useState(false);
  const [providerError, setProviderError] = useState<string | null>(null);
  /**
   * Mientras se valida la eleccion guardada del paso 0. Evita que dos pulsaciones
   * rapidas de "Enroll Now" disparen dos consultas y dos navegaciones.
   */
  const [providerChecking, setProviderChecking] = useState(false);
  const { user, logout } = useLogin();
  const save = useRef(null);
  const { openDialog } = useDialog();
  const {
    enrollData,
    enrollDataUsio,
    saveEnrrollData,
    saveEnrrollDataUsio,
    stateDwolla,
    setStateDwolla,
    stateUsio,
    setStateUsio,
    providerChoice,
    setProviderChoice,
  } = useEnrollForm();
  const [activeStep, setActiveStep] = useState(0);
  const [isResuming, setIsResuming] = useState(true);
  const [enabledProviderCount, setEnabledProviderCount] = useState(0);
  const isPaymentSetup = source === 'payment-setup';
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = [
    { label: 'Select Enrollment Type', icon: <SelectTypeIcon /> },
    { label: 'Complete Form', icon: <CompleteFormIcon /> },
    { label: 'Beneficial Owners Information', icon: <CompleteFormIcon /> },
    { label: 'Setup Merchant Processor', icon: <Send /> },
    { label: 'Funding Setup', icon: <MoneyOutlined /> },
    { label: 'Finish Account Setup', icon: <ValidateIcon /> },
  ];

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const defaultOptionsReview = {
    loop: true,
    autoplay: true,
    animationData: animationDataReview,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  /**
   * Propósito: Reanuda el wizard tras un refresh.
   * Hidrata el KYC desde el request y elige el paso con backend + localStorage,
   * para no volver al paso 0 (el status_dwolla solo no cubre Usio-only ni el form).
   */
  const resumeEnrollment = async () => {
    if (user === undefined) {
      /**
       * Output:
       * Side effects — Auth todavía hidrata; se mantiene el spinner
       */
      return;
    }

    if (!user?.token) {
      /**
       * Output:
       * Side effects — Sin sesión se muestra el login, paso 0
       */
      setActiveStep(0);
      setIsResuming(false);
      return;
    }

    setIsResuming(true);
    const stored = readEnrollmentProgress(user.user_id);
    const result = await fetchRequestByUser(user);

    if (result.status === 'expired') {
      /**
       * Output:
       * Side effects — Cierra sesión; el token ya no sirve para reanudar
       */
      logout();
      setIsResuming(false);
      return;
    }

    const request = result.status === 'ok' ? result.request : null;

    if (request) {
      if (request.info_dwolla) saveEnrrollData(request.info_dwolla);
      if (request.info_usio) saveEnrrollDataUsio(request.info_usio);
      setStateDwolla(request.status_dwolla_id || 0);
      setStateUsio(request.status_usio_id || 0);
    } else {
      if (providerChoice === DEFERRED_OPTION) {
        setProviderChoice(null);
      }
      setStateDwolla(0);
      setStateUsio(0);
    }

    if (isPaymentSetup) {
      const enabledProviders = await fetchEnabledProviders();
      setEnabledProviderCount(enabledProviders.length);
      const resolvedSetup = resolvePaymentSetupProgress({
        stored,
        enabledCodes: enabledProviders.map((provider) => provider.code),
        currentChoice:
          (request?.providerChoice as ProviderSelection | null)
          || (request?.provider_choice as ProviderSelection | null)
          || providerChoice,
      });
      if (resolvedSetup.providerChoice) {
        setProviderChoice(resolvedSetup.providerChoice);
      } else if (providerChoice === DEFERRED_OPTION) {
        setProviderChoice(null);
      }
      setActiveStep(resolvedSetup.step);
      setEnrollmentType(resolvedSetup.enrollmentType || 'business');
      setDisplayNavigationButtons(resolvedSetup.showNav);
      /**
       * Output:
       * Side effects — KYC del portal: formulario real, sin cards si solo hay Usio
       */
      setIsResuming(false);
      return;
    }

    const resolved = resolveEnrollmentProgress({ request, stored });
    setActiveStep(resolved.step);
    if (resolved.enrollmentType) setEnrollmentType(resolved.enrollmentType);
    setDisplayNavigationButtons(resolved.showNav);
    /**
     * Output:
     * Side effects — activeStep, tipo y datos de KYC restaurados
     */
    setIsResuming(false);
  };

  /**
   * Propósito: Manda al paso de crear subdominio/submerchant (FundingStep).
   * Tras enviar el KYC ya no se espera en "Under Review": el portal se arma
   * enseguida y la revisión interna sigue en paralelo.
   */
  const goToSubdomainSetup = () => {
    /**
     * Output:
     * Side effects — activeStep 16, sin botones Back/Next del wizard
     */
    setActiveStep(16);
    setDisplayNavigationButtons(false);
  };

  useEffect(() => {
    resumeEnrollment();
    // Solo al montar o al cambiar de usuario: si depende de stateDwolla, un
    // guardado del form saltaría de paso a mitad de completar el KYC.
  }, [user?.token, user?.user_id, source]);

  useEffect(() => {
    if (isResuming || !user?.user_id) return;
    persistEnrollmentProgress(user.user_id, {
      step: activeStep,
      type: enrollmentType,
    });
  }, [activeStep, enrollmentType, user?.user_id, isResuming]);

  const requiredImagesFor = (typeImage?: string) =>
    typeImage === 'passport' ? 1 : typeImage ? 2 : 1;

  const hasCompleteDocs = (holder: any): boolean => {
    if (!holder?.userIdImgs?.length) return false;
    return holder.userIdImgs.length >= requiredImagesFor(holder.typeImage);
  };

  const getPrimaryDocumentHolder = (data: any) => {
    if (hasCompleteDocs(data)) return data;
    if (hasCompleteDocs(data?.controller)) return data.controller;
    if (data?.userIdImgs?.length) return data;
    return data?.controller || data;
  };

  const normalizePrimaryDocuments = (data: any) => {
    const normalized = JSON.parse(JSON.stringify(data));
    const controller = normalized?.controller;

    if (!hasCompleteDocs(normalized) && hasCompleteDocs(controller)) {
      normalized.userIdImgs = [...controller.userIdImgs];
      normalized.typeImage = controller.typeImage;
    }

    if (normalized?.userIdImgs?.length && controller) {
      delete controller.userIdImgs;
      delete controller.typeImage;
    }

    return normalized;
  };

  const hanleSubmitForm = () => {
    let totalOwnershipPercent = 0;

    if (enrollData.data.controller) {
      totalOwnershipPercent += parseFloat(enrollData.data.controller.ownershipPercent);

      enrollData.data.owners.forEach((owner: { ownershipPercent: string }) => {
        totalOwnershipPercent += parseFloat(owner.ownershipPercent);
      });

      if (totalOwnershipPercent >= 25) {
        if (totalOwnershipPercent < 100) {
          openDialog('information', null, {
            title: 'Ownership Percentage Notice',
            icon: <Info />,
            cancelText: 'Ok',
            description: `The total ownership percentage meets the minimum requirement of 25%. It currently totals ${totalOwnershipPercent}%. You can still add more owners until the total reaches 100%.`,
          });
        }
      } else {
        openDialog('information', null, {
          title: 'Application Incomplete',
          icon: <Info />,
          cancelText: 'Ok',
          description: `The total percentage is less than the minimum required 25%. It currently totals ${totalOwnershipPercent}%.`,
        });
        return;
      }
    }

    // Main applicant: must have all required ID images for their chosen document type.
    const primaryDocumentHolder = getPrimaryDocumentHolder(enrollData?.data);
    if (!hasCompleteDocs(primaryDocumentHolder)) {
      const need = requiredImagesFor(primaryDocumentHolder?.typeImage);
      openDialog('information', null, {
        title: 'Application incomplete',
        icon: <Info />,
        cancelText: 'Ok',
        description:
          need === 2
            ? 'You need to upload BOTH the front and the back of your ID/Driver License before submitting.'
            : 'You need to upload your Passport (or both sides of your ID/Driver License) before submitting.',
      });
      return;
    }

    // Each owner must have all required ID images too.
    for (const owner of enrollData?.data?.owners || []) {
      if (!hasCompleteDocs(owner)) {
        const need = requiredImagesFor(owner.typeImage);
        openDialog('information', null, {
          title: 'Application incomplete',
          icon: <Info />,
          cancelText: 'Ok',
          description:
            need === 2
              ? `Owner "${owner.firstName || ''} ${owner.lastName || ''}" needs BOTH the front and the back of their ID/Driver License.`
              : `Owner "${owner.firstName || ''} ${owner.lastName || ''}" needs to upload at least one document (Passport or both sides of ID/Driver License).`,
        });
        return;
      }
    }

    openDialog('information', null, {
      title: 'Submit application',
      icon: <Info />,
      cancelText: 'Cancel',
      description:
        'Want to submit your application? Do not worry, we will notify you if something needs fixing.',
      onConfirm: confirmSubmition,
    });
  };

  /**
   * Propósito: Envía el enrollment interno con info_dwolla e info_usio.
   * Completa dbaName desde doingBusinessAs/legalName para que USIO no reciba DBA vacío.
   */
  const confirmSubmition = async () => {
    try {
      const normalizedDwollaData = normalizePrimaryDocuments(enrollData.data);
      const usioPayload = { ...enrollDataUsio.data };
      usioPayload.dbaName = resolveMerchantDbaName(usioPayload, enrollData.data);
      await updateEnroll(user, {
        user_id: user?.user_id,
        /**
         * Se manda la eleccion explicita (`provider_choice`) en vez de los flags
         * sueltos: el backend deriva `is_usio`/`is_dwolla` de ella y asi el
         * request queda con el proveedor correcto, no siempre con los dos.
         *
         * Si la sesion no trae eleccion (p. ej. un borrador viejo reanudado) se
         * mantiene el comportamiento anterior y se activan ambos.
         */
        provider_choice: toBackendChoice(providerChoice),
        deferred: false,
        origin: isPaymentSetup ? REQUEST_ORIGIN.SUBDOMAIN : REQUEST_ORIGIN.LANDING,
        ...(providerChoice
          ? providerFlags(providerChoice)
          : { is_usio: true, is_dwolla: true }),
        info_dwolla: normalizedDwollaData,
        info_usio: usioPayload,
      });

      saveEnrrollData(normalizedDwollaData);
      if (isPaymentSetup) {
        /**
         * Output:
         * Side effects — El portal ya existe; el padre muestra "en revisión"
         */
        onSubmitted?.();
        return;
      }
      goToSubdomainSetup();
      /**
       * Output:
       * Side effects — Persiste info_usio.dbaName y abre el paso de subdominio
       */
    } catch (error) {
      /**
       * Output:
       * Side effects — Loguea el error y deja al usuario en el mismo paso
       */
      console.log('confirmSubmition', error);
    }
  };

  /**
   * Crea la solicitud cuando el abogado eligio "configurar despues".
   *
   * Hace falta porque el paso del subdominio (`FundingStep`) necesita que exista
   * un request: `POST /api/subdomain/create-subdomain` lo resuelve desde el token
   * y responde 400 si el usuario no tiene ninguno.
   *
   * `POST /api/requests` rechaza si el usuario ya tiene una solicitud, asi que
   * primero se consulta. Es idempotente a proposito: volver atras y reintentar no
   * debe romper.
   */
  const ensureDeferredRequest = async () => {
    const existing = await fetchRequestByUser(user);
    if (existing.status === 'expired') {
      logout();
      /**
       * Output:
       * @throws Error — Sesión inválida; el paso de proveedor muestra el mensaje
       */
      throw new Error('Your session expired. Please sign in again.');
    }
    if (existing.status === 'ok' && existing.request.id) {
      /**
       * Output:
       * @returns UserRequest — Solicitud diferida que ya existía (idempotente)
       */
      return existing.request;
    }

    const response = await fetch(`${newServer}requests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user?.token}`,
      },
      body: JSON.stringify({
        user_id: user?.user_id,
        // `null` = "configurar despues"; el backend lo marca como diferido.
        provider_choice: null,
        deferred: true,
        origin: REQUEST_ORIGIN.LANDING,
      }),
    });

    if (!response.ok) {
      const detail = await response.text();
      /**
       * Output:
       * @throws Error — El POST falló (usuario duplicado, validación, etc.)
       */
      throw new Error(detail || 'Could not create your application.');
    }

    const created = await fetchRequestByUser(user);
    /**
     * Output:
     * @returns UserRequest | undefined — Request diferida recien creada, o
     *   undefined si el POST fue OK pero la lectura posterior falló
     */
    return created.status === 'ok' ? created.request : undefined;
  };

  /**
   * Continuacion del paso de proveedor.
   *
   * Camino diferido: se crea la solicitud y se salta directo al subdominio, sin
   * pedir KYC. El abogado entra a su portal enseguida y completa Usio/Dwolla
   * despues desde `/payment-setup`.
   *
   * Camino normal: sigue el formulario de siempre.
   */
  const handleProviderContinue = async (choice: ProviderSelection) => {
    setProviderError(null);
    setProviderChoice(choice);

    if (choice !== DEFERRED_OPTION) {
      setActiveStep(1);
      setEnrollmentType('business');
      setDisplayNavigationButtons(true);
      return;
    }

    if (isPaymentSetup) {
      /**
       * Output:
       * Side effects — En el portal no se permite diferir otra vez
       */
      return;
    }

    setProviderSaving(true);

    try {
      await ensureDeferredRequest();
      goToSubdomainSetup();
    } catch (error: any) {
      console.log('handleProviderContinue', error);
      setProviderError(
        error?.message || 'We could not create your application. Please try again.',
      );
    } finally {
      setProviderSaving(false);
    }
  };

  const handleNext = () => {
    if (!enrollData || (enrollData.data?.isValid && activeStep === 1)) {
      return;
    }

    if (activeStep === 2) {
      hanleSubmitForm();
      return;
    }

    let currentStep = activeStep + 1;

    setActiveStep(currentStep);

    if (user?.token) {
      if (save?.current) {
        (save as any)?.current();
      }
      setDisplayNavigationButtons(true);
    } else {
      setDisplayNavigationButtons(false);
    }
  };

  /**
   * Propósito: Retrocede un paso. En `/payment-setup` no vuelve al
   * "Enroll Now" ni a las cards si solo hay un proveedor habilitado.
   */
  const handleBack = () => {
    if (isPaymentSetup && activeStep === 1) {
      if (enabledProviderCount > 1) {
        /**
         * Output:
         * Side effects — Vuelve a elegir proveedor (solo si hay más de uno)
         */
        setActiveStep(PROVIDER_STEP);
        setDisplayNavigationButtons(false);
      }
      /**
       * Output:
       * Side effects — Un solo proveedor: se queda en el formulario de KYC
       */
      return;
    }

    /**
     * Output:
     * Side effects — Paso anterior del wizard de onboarding
     */
    setActiveStep(activeStep - 1);
  };

  const navigateHome = () => {
    logout();
    navigate('/');
  };

  /**
   * Propósito: Los pasos 0, 10, 13, 16 y el de proveedor tienen su propia CTA.
   * Si Back y Submit se posicionan cada uno con `right` fijo, "Submit Application"
   * es más ancho que "Next" y tapa el Back (el botón claro que asomaba debajo).
   */
  const shouldShowWizardNav = Boolean(
    displayNavigationButtons
      && user
      && activeStep !== 0
      && activeStep !== 16
      && activeStep !== 10
      && activeStep !== 13
      && activeStep !== PROVIDER_STEP,
  );

  const isNextDisabled = !enrollData || enrollData.data?.isValid || submitionOnReview;
  const wizardNextLabel = activeStep === 2
    ? 'Submit Application'
    : activeStep === steps.length - 1
      ? 'Sent Application'
      : 'Next';

  const renderStepContent = (stepIndex: number) => {
    if (!isNaN(stepIndex)) {
      switch (stepIndex) {
        case 0:
          return (
            <Grid item xs={12} md={12} lg={12} className='center-item'>
              <Card className='card-custom'>
                <CardContent>
                  <Icon>
                    <Business />
                  </Icon>
                  <Typography variant='h5'>Organization</Typography>
                  <Typography variant='body2'>
                    Data and Documents of your organization. Yes, we need them to finish the
                    Enrollment Process. Lets get it out of the way to get you paid!
                  </Typography>
                  <RoundedButton
                    variant='contained'
                    color='primary'
                    disabled={providerChecking}
                    onClick={async () => {
                      setEnrollmentType('business');
                      /**
                       * Si ya eligio proveedor, se salta el paso para no
                       * repetirlo al volver atras o al recargar la pagina.
                       * En el camino diferido va al subdominio, no al KYC.
                       *
                       * La eleccion guardada se valida aqui, en el punto de
                       * decision: el descarte de `ProviderSelectionStep` solo
                       * corre al montarse ese paso, y este camino lo salta, asi
                       * que una eleccion caduca llegaria intacta al KYC.
                       */
                      if (providerChoice === DEFERRED_OPTION) {
                        /**
                         * Solo se salta el KYC si YA existe la solicitud diferida.
                         * Si solo quedó la eleccion en localStorage, es una
                         * postulación nueva y hay que elegir proveedor otra vez.
                         * Saltar aquí montaba FundingStep como si el portal ya
                         * existiera y el loading nunca terminaba.
                         */
                        setProviderChecking(true);
                        try {
                          const existingRequest = await fetchRequestByUser(user);
                          if (existingRequest.status === 'expired') {
                            logout();
                            return;
                          }
                          if (existingRequest.status === 'ok' && existingRequest.request.id) {
                            goToSubdomainSetup();
                          } else {
                            setActiveStep(PROVIDER_STEP);
                            setDisplayNavigationButtons(true);
                          }
                        } finally {
                          setProviderChecking(false);
                        }
                      } else if (providerChoice) {
                        setProviderChecking(true);
                        try {
                          const stillAvailable = await isProviderEnabled(providerChoice);
                          if (stillAvailable) {
                            setActiveStep(1);
                            setDisplayNavigationButtons(true);
                          } else {
                            /**
                             * El admin deshabilito el proveedor elegido: se
                             * limpia la eleccion (tambien de localStorage) y se
                             * le pide elegir de nuevo.
                             */
                            setProviderChoice(null);
                            setActiveStep(PROVIDER_STEP);
                            setDisplayNavigationButtons(true);
                          }
                        } finally {
                          setProviderChecking(false);
                        }
                      } else {
                        setActiveStep(PROVIDER_STEP);
                        setDisplayNavigationButtons(true);
                      }
                      setProviderError(null);
                    }}
                  >
                    Enroll Now
                  </RoundedButton>
                </CardContent>
              </Card>
            </Grid>
          );
        case 1:
          return (
            <>
              {!submitionOnReview && (
                <div>
                  {enrollmentType === 'business' && (
                    <CompanyForm
                      save={save}
                      setSubmitionOnReview={setSubmitionOnReview}
                      setDataLoaded={setDataLoaded}
                      dataLoaded={dataLoaded}
                    />
                  )}
                </div>
              )}
            </>
          );
        case 2:
          return (
            <Card>
              <FormValidation />
            </Card>
          );
        case 3:
          /**
           * `bg-gray-100` pintaba NEGRO (la escala `gray` del config resolvia
           * `gray.100` a negro puro) donde se esperaba un gris claro, y el otro
           * fondo era un hex de cinco digitos, que ni siquiera es valido (el
           * navegador lo descarta). Ahora la tarjeta es `surface` y los bloques
           * anidados `surface-raised`, que es la escalera de elevacion real del
           * tema.
           */
          return (
            <Card>
              {stateDwolla !== 3 ? (
                <div className='bg-surface-raised'>
                  {!submitionOnReview ? (
                    <CardContent className='bg-surface-raised'>
                      <Typography
                        variant='h1'
                        align='center'
                        style={{
                          fontSize: '30px',
                          fontWeight: 'bold',
                          marginTop: '25px',
                        }}
                      >
                        Application : Under Review
                      </Typography>
                      <Typography
                        variant='h4'
                        align='center'
                        style={{ fontSize: '20px', marginTop: '10px' }}
                      >
                        Our team is exited to welcome you! They are working around the clock to
                        ensure we get everything to get you processing payments asap!
                      </Typography>
                      <Lottie options={defaultOptions} height={300} width={300} />
                      <Grid style={{ justifyContent: 'center', display: 'flex' }}>
                        <Button
                          variant='contained'
                          color='primary'
                          startIcon={<Person />}
                          className='add-user-button'
                          onClick={navigateHome}
                          style={{ margin: '10px' }}
                        >
                          Return Home
                        </Button>
                      </Grid>
                    </CardContent>
                  ) : (
                    <Card>
                      <Grid>
                        <Box>
                          <Lottie options={defaultOptionsReview} height={300} width={300} />
                        </Box>
                        <Typography
                          variant='h4'
                          align='center'
                          style={{
                            fontSize: '15px',
                            margin: '25px',
                          }}
                        >
                          Enrollment application successfully uploaded , and it's currently under
                          review. Expect to receive an update shortly.
                        </Typography>
                      </Grid>
                    </Card>
                  )}
                </div>
              ) : (
                <RejectedEnrolled />
              )}
            </Card>
          );
        case 4:
          return (
            <Card>
              <ClickAgreeStep />
            </Card>
          );
        case 5:
          return <ConfirmationMenssage />;
        case 9:
          return <PlaidStep setActiveStep={setActiveStep} />;
        case 10:
          return (
            <div style={{ textAlign: 'center' }}>
              <Card>
                <MenssgePostDay />
              </Card>
            </div>
          );
        case 12:
          return <PlaidStep setActiveStep={setActiveStep} />;
        case 13:
          return <PlaidStep setActiveStep={setActiveStep} />;
        case 16:
          return <FundingStep />;
        case PROVIDER_STEP:
          return (
            <ProviderSelectionStep
              value={providerChoice}
              onContinue={handleProviderContinue}
              error={providerError}
              saving={providerSaving}
              showDeferred={!isPaymentSetup}
            />
          );
        default:
          return 'Unknown step';
      }
    } else {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '800px',
            width: '100%',
          }}
        >
          <CircularProgress />
        </div>
      );
    }
  };

  return (
    <>
      <Container>
        {!isResuming && activeStep != 0 && !isPaymentSetup && (
          <>
            <Stack sx={{ width: '100%' }} spacing={4}>
              <Stepper activeStep={activeStep} alternativeLabel connector={<QontoConnector />}>
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepLabel
                      sx={{
                        '.MuiStepLabel-label': {
                          /**
                           * El blanco forzado con `!important` dejaba las
                           * etiquetas blancas tambien en tema claro
                           * (desaparecian). Se mantiene el `!important` porque
                           * MUI pinta los pasos completados con
                           * `text.disabled`, pero ahora el valor sale del tema.
                           */
                          color: `${theme.palette.text.primary} !important`,
                        },
                      }}
                    >
                      {isMobile ? (index === activeStep ? step.label : null) : step.label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Stack>
          </>
        )}

        {!isResuming && shouldShowWizardNav && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 20,
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              ...(isMobile
                ? { left: 16, right: 16, justifyContent: 'space-between' }
                : { right: 20 }),
            }}
          >
            {/* `bg-black`/`text-white` pisaban el relleno y el contraste del tema:
                el boton ya es `color='primary'`, asi que el color lo pone el tema. */}
            <Fab
              type='button'
              variant='extended'
              color='primary'
              onClick={handleBack}
            >
              <ArrowLeft />
              Back
            </Fab>
            {/*
              El CTA era un verde lima literal con texto negro. Ahora el
              habilitado usa el rol `success` y el deshabilitado lo resuelve MUI.
              Van en el mismo flex que Back para que "Submit Application" no
              lo cubra: antes Back iba a `right: 130px` y este a `right: 20px`.
            */}
            <Fab
              type='button'
              variant='extended'
              color={isNextDisabled ? 'default' : 'success'}
              disabled={isNextDisabled}
              onClick={handleNext}
              sx={{ fontWeight: 700 }}
            >
              {wizardNextLabel}
              <ArrowRight />
            </Fab>
          </Box>
        )}
        {isResuming ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
              width: '100%',
            }}
          >
            <CircularProgress />
          </div>
        ) : !user?.token ? (
          <AuthPage />
        ) : (
          renderStepContent(activeStep)
        )}
      </Container>
    </>
  );
};

export default EnrollmentForm;
